import { useState, useEffect } from 'react';
import { AppBar, Toolbar, Stack, Button, IconButton, useMediaQuery, useTheme, Box } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation } from 'react-router';
import { dictionary } from '../../dictionary';
import { palette } from '../../theme';
import MobileDrawer from './MobileDrawer';
import type { NavbarProps } from './Navbar.types';
import { navItems } from '../shared/navItems';

export default function Navbar({ isHomepage }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('laptop'));
  const { pathname } = useLocation();

  const isActive = (path: string) =>
    path === '/articles' ? pathname.startsWith('/articles') : pathname === path;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isTransparent = isHomepage && !scrolled;
  const bgColor = isTransparent ? 'transparent' : palette.backgroundPrimary;
  const textColor = isTransparent ? palette.backgroundPrimary : palette.secondary;

  return (
    <>
      <AppBar
        position='fixed'
        elevation={0}
        sx={{
          backgroundColor: bgColor,
          boxShadow: scrolled ? '0px 12px 24px 0px rgba(44, 44, 44, 0.27)' : 'none',
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            flexDirection: { mobile: 'row-reverse', laptop: 'row' },
            px: { mobile: '16px', laptop: '50px' },
            pt: { mobile: '16px', laptop: '28px' },
            pb: { laptop: isTransparent ? 'unset' : '28px', mobile: '16px' },
          }}
        >
          <Stack direction='row' alignItems='center' gap={1}>
            <Box component={Link} to='/' sx={{ maxHeight: { laptop: '31.39px', mobile: '18.83px' } }}>
              <Box
                component='img'
                src={isTransparent ? '/images/logo.png' : '/images/logo-brown.png'}
                alt={dictionary.general.firmName}
                sx={{
                  width: { laptop: '200px', mobile: '120px' },
                  // filter: isTransparent ? 'brightness(10)' : 'none',
                  transition: 'filter 0.3s ease',
                  opacity: isTransparent ? 0.8 : 1,
                  '&:hover': {
                    filter: 'none',
                  },
                }}
              />
            </Box>
          </Stack>

          {isMobile ? (
            <IconButton onClick={() => setDrawerOpen(true)} aria-label={dictionary.general.aria.menu} sx={{ color: textColor }}>
              <MenuIcon />
            </IconButton>
          ) : (
            <Stack component='nav' aria-label={dictionary.general.aria.mainNav} direction='row' alignItems='center' gap={2}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: textColor,
                    fontWeight: isActive(item.path) ? 300 : 200,
                    fontSize: '26px',
                    borderRadius: 0,
                    borderBottom: '2px solid transparent',
                    borderColor: isActive(item.path) ? palette.accent : 'transparent',
                    transition: 'border-color 0.3s ease, font-weight 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      borderColor: palette.accent,
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Stack>
          )}
        </Toolbar>
      </AppBar>
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}
